import { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getTokenFromStorage } from '@/utils/auth';
import { marketplaceRequest, type MarketplaceConnection, type MarketplaceSearch } from '@/api/creatorApi';
import { Button } from '@/components/ui/button';

/** Staging consent and creator evaluation; no publishing or paid-report actions. */
export function CreatorMarketplacePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const key = `marketplace-session:${user?.user_id ?? 'signed-out'}:${user?.team?.team_id ?? 'no-team'}`;
  const actor = useRef(key);
  actor.current = key;
  const lifecycle = useRef(0);
  const callbackSession = useRef(params.get('marketplace_session'));
  const [sessionId, setSessionId] = useState<string>();
  const [connection, setConnection] = useState<MarketplaceConnection>();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MarketplaceSearch>();

  const run = useCallback(async (work: (token: string, current: () => boolean) => Promise<void>) => {
    const owner = key;
    const generation = lifecycle.current;
    const current = () => actor.current === owner && lifecycle.current === generation;
    const token = getTokenFromStorage();
    if (!token) { setError(t('creatorMarketplace.signIn')); return; }
    setError(''); setBusy(true);
    try { await work(token, current); }
    catch { if (current()) setError(t('creatorMarketplace.requestFailed')); }
    finally { if (current()) setBusy(false); }
  }, [key, t]);

  useLayoutEffect(() => {
    let active = true;
    const token = getTokenFromStorage();
    setConnection(undefined); setResult(undefined); setAvailable(false); setSessionId(undefined); setBusy(false); setError('');
    // Invalidate pending actions on cleanup, even if a user switches away and back.
    const cancel = () => { active = false; lifecycle.current += 1; };
    if (!token || !user) return cancel;
    const returned = callbackSession.current;
    callbackSession.current = null;
    const stored = sessionStorage.getItem(key);
    const id = returned ?? stored;
    if (id && /^[A-Za-z0-9_-]{43}-mco$/.test(id)) {
      sessionStorage.setItem(key, id); setSessionId(id);
    }
    void marketplaceRequest<{ available: boolean }>(token, 'availability').then(async ({ available: enabled }) => {
      if (!active) return;
      setAvailable(enabled);
      if (enabled && id && /^[A-Za-z0-9_-]{43}-mco$/.test(id)) {
        const value = await marketplaceRequest<MarketplaceConnection>(token, 'session', { sessionId: id });
        if (active) setConnection(value);
      }
    }).catch(() => { if (active) setError(t('creatorMarketplace.expired')); });
    return cancel;
    // The callback query is consumed once; user/key changes clear the displayed connection.
  }, [key, user?.user_id]);

  // Remove the opaque callback handle from browser history after ownership binding.
  useEffect(() => {
    if (params.has('marketplace_session')) {
      const next = new URLSearchParams(params);
      next.delete('marketplace_session');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const connect = () => run(async (token, current) => {
    const response = await marketplaceRequest<{ url: string }>(token, 'authorize', { method: 'POST' });
    if (!current()) return;
    const { url } = response;
    // Pin the consent destination and exact approved scopes before leaving Beatly.
    const target = new URL(url);
    const scopes = new Set((target.searchParams.get('scope') ?? '').split(','));
    const required = ['instagram_basic', 'instagram_creator_marketplace_discovery', 'pages_manage_metadata', 'pages_show_list', 'business_management'];
    if (target.username || target.password || target.searchParams.get('response_type') !== 'code' ||
        !/^[A-Za-z0-9_-]{43}-mco$/.test(target.searchParams.get('state') ?? '') ||
        scopes.size !== required.length || required.some((scope) => !scopes.has(scope)) ||
        target.origin !== 'https://www.facebook.com' || target.pathname !== '/v25.0/dialog/oauth' ||
        target.searchParams.get('client_id') !== '1538955394387884' ||
        target.searchParams.get('redirect_uri') !== 'https://api-creator.staging.beatly.com/omni/v1/creator-marketplace/callback') {
      throw new Error('Unexpected authorization destination');
    }
    if (current()) window.location.assign(target.href);
  });
  const disconnect = () => run(async (token, current) => {
    if (sessionId) await marketplaceRequest(token, 'disconnect', { method: 'POST', body: { session_id: sessionId } });
    sessionStorage.removeItem(key);
    if (current()) { setSessionId(undefined); setConnection(undefined); setResult(undefined); }
  });

  return <main className="mx-auto max-w-3xl space-y-6 p-6" aria-label={t('creatorMarketplace.title')}>
    <header><h1 className="text-2xl font-semibold">{t('creatorMarketplace.title')}</h1>
      <p className="mt-2 text-muted-foreground">{t('creatorMarketplace.intro')}</p></header>
    <p>{t('creatorMarketplace.testNotice')}</p>
    {error && <p role="alert">{error}</p>}
    {!available ? <p role="status">{t('creatorMarketplace.disabled')}</p> : <>
      <Button onClick={connect} disabled={busy}>{connection ? t('creatorMarketplace.reconnect') : t('creatorMarketplace.connect')}</Button>
      {connection?.status === 'declined' && <p role="status">{t('creatorMarketplace.cancelled')}</p>}
      {connection?.status === 'failed' && <p role="status">{t('creatorMarketplace.verificationFailed')}</p>}
      {connection?.status === 'authorizing' && <p role="status">{t('creatorMarketplace.incomplete')}</p>}
      {connection?.assets.map((asset) => <section key={asset.page_id} className="rounded-lg border p-4 space-y-3">
        <h2 className="text-lg font-semibold">{asset.page_name}</h2><p>{t('creatorMarketplace.instagram', { username: asset.instagram_username })}</p>
        <details><summary>{t('creatorMarketplace.permissions')}</summary><ul>{asset.granted_permissions.map((scope) => <li key={scope}>{scope}</li>)}</ul></details>
        {connection.status === 'select' && <Button disabled={busy} onClick={() => run(async (token, current) => {
          setResult(undefined);
          const value = await marketplaceRequest<MarketplaceConnection>(token, 'select', { method: 'POST', body: { session_id: sessionId, page_id: asset.page_id } });
          if (current()) setConnection(value);
        })}>{t('creatorMarketplace.select')}</Button>}
        {connection.status === 'connected' && <>
          <p role="status">{t('creatorMarketplace.connected')}</p>
          <div className="flex flex-wrap gap-3">{(['total_followers', 'creator_reach'] as const).map((metric) => <Button key={metric} disabled={busy} onClick={() => run(async (token, current) => {
            setResult(undefined);
            const value = await marketplaceRequest<MarketplaceSearch>(token, 'search', { sessionId, metric });
            if (current()) setResult(value);
          })}>{metric === 'total_followers' ? t('creatorMarketplace.findFollowers') : t('creatorMarketplace.findReach')}</Button>)}</div>
        </>}
      </section>)}
      {sessionId && <Button variant="outline" disabled={busy} onClick={disconnect}>{t('creatorMarketplace.disconnect')}</Button>}
    </>}
    {busy && <p role="status">{t('creatorMarketplace.working')}</p>}
    {result && <section className="space-y-4" aria-label={t('creatorMarketplace.results')}>
      <p>{t('creatorMarketplace.source', { source: result.origin === 'meta_standard_access' ? t('creatorMarketplace.liveSource') : t('creatorMarketplace.capturedSource') })}</p>
      {result.data.length === 0 && <p role="status">{t('creatorMarketplace.noCreators')}</p>}
      {result.data.map((creator) => <article key={creator.id} className="rounded-lg border p-4">
        <h2 className="font-semibold">@{creator.username}</h2><p>{creator.biography ?? t('creatorMarketplace.biographyUnavailable')}</p>
        {creator.insights?.map((insight) => <p key={insight.name}>{insight.name === 'total_followers' ? t('creatorMarketplace.followers') : t('creatorMarketplace.reach')}: {insight.value === null ? t('creatorMarketplace.unavailable') : insight.value.toLocaleString('en-US')} — {insight.time_range === 'lifetime' ? t('creatorMarketplace.lifetime') : t('creatorMarketplace.last14Days')} ({insight.period})</p>)}
      </article>)}
      <p>{t('creatorMarketplace.evaluation')}</p>
    </section>}
  </main>;
}
